import React, { useEffect, useState } from "react";
import { useGetVolumeBreakdownsMutation } from "../../../store/api/lumi/volume";
import { getConceptFilter } from "../../../utils/getConceptFilter";
import { debounce } from "lodash";
import { Divider, Spin } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import classes from "./MetadataConceptBreakdown.module.css";
import Scrollbars from "react-custom-scrollbars-2";
import { FilterNameSelector } from "../../DashboardComponents/DashboardFilters/shared";

const MetadataConceptBreakdown = (props) => {
    const { filters, metaDataQu, breakdownName, setBreakdownName } = props;
    const [getTotals] = useGetVolumeBreakdownsMutation();
    const [loading, setLoading] = useState(false);
    const metaData = metaDataQu.metaDataWithStringType;
    const [processedData, setProcessedData] = useState([]);

    async function getVolumeData() {

        const conceptFilter = getConceptFilter(props);
        const { data: dataConcepts } = await getTotals({
            id: props.id,
            concept_selector: conceptFilter,
            filter: props.filters,
            breakdowns: [
                { name: Array.isArray(breakdownName) ? breakdownName[0] : breakdownName },
            ],
        });
        props.setFilterDocCount(dataConcepts.filter_count);
        if (props && props.conceptList) {
            props.setLocalConceptOptions(dataConcepts.match_counts);
        }
        const breakdownData = dataConcepts.breakdowns[0].buckets;
        const allMatchCounts = breakdownData
            .flatMap(({ match_counts }) => match_counts)
            .map(({ match_count }) => match_count);

        const globalMaxMatchCount = Math.max(...allMatchCounts);

        const aggregatedData = breakdownData
            .filter(({ match_counts }) => !match_counts.every(({ match_count }) => match_count === 0))
            .map(({ label, match_counts }) => {
                const sortedMatchCounts = [...match_counts].sort((a, b) => b.match_count - a.match_count);


                const totalMatchCount = match_counts.reduce((sum, { match_count }) => sum + match_count, 0);

                return {
                    label,
                    totalMatchCount,
                    matchCounts: sortedMatchCounts.map(({ name, match_count, exact_match_count }) => ({
                        name,
                        match_count,
                        exact_match_count,
                        conceptual_match_count: match_count - exact_match_count,
                        exact_match_percentage: (exact_match_count / match_count) * 100 || 0,
                        conceptual_match_percentage: ((match_count - exact_match_count) / match_count) * 100 || 0,
                        barWidth: (match_count / globalMaxMatchCount) * 100,
                    })),
                };
            })
            .sort((a, b) => b.totalMatchCount - a.totalMatchCount)
            .slice(0, 100);

        setProcessedData(aggregatedData);
        setLoading(false);
    }

    useEffect(() => {
        setLoading(true);
        const debouncedGetVolumes = debounce(getVolumeData, 500);
        debouncedGetVolumes();
        return () => {
            debouncedGetVolumes.cancel();
        };
    }, [
        filters,
        breakdownName,
        props.conceptTypeDay,
        props.selectedLocalConcepts,
        props.conceptList,
    ]);

    useEffect(() => {
        props.setAdditionalFilters([{
            key: "Special",
            label: "Select Name:",
            showArrow: false,
            children: <FilterNameSelector
                selectedFieldName={breakdownName}
                setSelectedFieldName={setBreakdownName}
                metaData={metaData}
            />,
        }]);
    }, [breakdownName]);

    if (loading) {
        return (
            <Spin
                className={"spinner-by-center"}
                indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
            />
        );
    }
    const defaultHeight = props.widget.layout.h * 40 - 85;

    return (
        <div className={classes.widgetContainer} style={{ height: defaultHeight }}>
            <div style={{ justifyContent: "flex-start" }}>
                <div className="sentiment exact">
                    <div className="color-box"></div>
                    Exact Matches
                </div>
                <div className="sentiment conceptual">
                    <div className="color-box"></div>
                    Conceptual Matches
                </div>
            </div>
            <Scrollbars style={{ height: "100%" }}>
                {processedData.length > 0 ? (
                    processedData.map((conceptGroup, index) => (
                        <div key={`group-${index}`} className={classes.conceptGroup}>
                            <div className={classes.groupLabel}>{conceptGroup.label}</div>
                            <div className={classes.gridContainer}>
                                <div className={classes.headerGridContainer}>
                                    <div className={classes.gridItemHeader}>Concept</div>
                                    <div className={classes.gridItemHeader}>Total Matches</div>
                                    <div className={classes.gridItemHeader}></div>
                                </div>
                                {conceptGroup.matchCounts?.map((match, i) => (
                                    <div key={`${conceptGroup.label}-${match.name}-${i}`} className={classes.gridRow}>
                                        <div className={classes.gridItem}>{match.name}</div>
                                        <div className={classes.gridItem}>
                                            {match.match_count}
                                        </div>
                                        <div
                                            className={classes.barContainer}
                                            style={{
                                                width: `${match.barWidth}%`,
                                                maxWidth: "100%",
                                            }}
                                        >
                                            <div
                                                className={classes.exactMatchBar}
                                                style={{
                                                    width: `${match.exact_match_percentage}%`,
                                                    backgroundColor: "#04aade",
                                                }}
                                                title={`Exact Matches: ${match.exact_match_count} (${match.exact_match_percentage.toFixed(1)}%)`}
                                            />
                                            <div
                                                className={classes.conceptualMatchBar}
                                                style={{
                                                    width: `${match.conceptual_match_percentage}%`,
                                                    backgroundColor: "#78d0ef",
                                                }}
                                                title={`Conceptual Matches: ${match.conceptual_match_count} (${match.conceptual_match_percentage.toFixed(1)}%)`}
                                            />
                                        </div>

                                    </div>
                                ))}
                            </div>
                            <Divider/>
                        </div>

                    ))
                ) : (
                    <div>No data available</div>
                )}
            </Scrollbars>

        </div>
    );
};

export default MetadataConceptBreakdown;
